import React, {useEffect, useState} from 'react';
import {Alert, Button, Empty, Input, message, Popconfirm, Space, Spin, Tag, Upload} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  ComplaintTaskDetailResponse,
  GET_COMPLAINT_TASK_DETAIL,
} from '../../../../graphql/complaintTask/queries';
import {
  RESOLVE_COMPLAINT_TASK,
  ResolveComplaintTaskVariables,
  START_COMPLAINT_TASK,
} from '../../../../graphql/complaintTask/mutations';
import {translateGraphQLError} from '../../../../utils/graphqlUtils';
import {formatDateTime, PRIORITY_COLOR, priorityLabel, subTypeLabel} from '../complaintTaskUtils';
import {MessageList, ProgressBar, ResolutionView} from './ComplaintTaskDetailPanel';
import {useComplaintAttachmentUpload} from '../useComplaintAttachmentUpload';

/**
 * 作业端右侧处理区（原型 image3 / image4）：开始处理（记响应，只认第一次）→ 写处理说明 + 粘贴截图 / 上传附件（≤5MB）→ 提交解决。
 * 是否可处理由后端判定（承接人，或有权限的客服 / 大区负责人 / +1 主管 / 系统管理员）。
 */
const ComplaintTaskWorkPanel: React.FC<{taskId: number; onChanged: () => void}> = ({taskId, onChanged}) => {
  const {t} = useTranslation();
  const [content, setContent] = useState('');
  const {files, uploading, upload, remove, toInputs} = useComplaintAttachmentUpload(taskId);

  const {data, loading, error, refetch} = useQuery<ComplaintTaskDetailResponse>(GET_COMPLAINT_TASK_DETAIL, {
    variables: {taskId},
    fetchPolicy: 'network-only',
  });
  const [startTask, {loading: starting}] = useMutation(START_COMPLAINT_TASK);
  const [resolveTask, {loading: resolving}] = useMutation<boolean, ResolveComplaintTaskVariables>(RESOLVE_COMPLAINT_TASK);

  // 切换任务时清空草稿（附件由 useComplaintAttachmentUpload 按 taskId 清）
  useEffect(() => {
    setContent('');
  }, [taskId]);

  const detail = data?.complaintTaskDetail;
  const task = detail?.task;

  // 截图直接 Ctrl/Cmd+V 粘贴
  const onPaste = (e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData.files);
    if (pasted.length) {
      e.preventDefault();
      pasted.forEach(upload);
    }
  };

  const onStart = async () => {
    try {
      await startTask({variables: {taskId}});
      await refetch();
      onChanged();
    } catch (e) {
      message.error(translateGraphQLError(e, 'complaintTask.errors.startFailed'));
    }
  };

  const onResolve = async () => {
    try {
      await resolveTask({
        variables: {
          input: {
            taskId,
            content: content.trim(),
            attachments: toInputs(),
          },
        },
      });
      message.success(t('complaintTask.work.resolved'));
      await refetch();
      onChanged();
    } catch (e) {
      message.error(translateGraphQLError(e, 'complaintTask.errors.resolveFailed'));
    }
  };

  if (error) return <Alert type="error" showIcon message={translateGraphQLError(error, 'complaintTask.errors.loadFailed')}/>;
  if (!detail || !task) return <Spin spinning={loading}><Empty/></Spin>;

  const processing = detail.canProcess && task.status === 40;

  return (
    <Spin spinning={loading}>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <Space size={4}>
            <Tag color={PRIORITY_COLOR[task.priority ?? 3]}>{priorityLabel(task.priority)}</Tag>
            {task.emotionLevel === 'BURST' && <Tag color="red">{t('complaintTask.emotion.BURST')}</Tag>}
            <Tag color="purple">{subTypeLabel(task.subType, task.subTypeName)}</Tag>
          </Space>
          <span className="text-gray-400 text-sm">#{task.taskId} · {t('complaintTask.work.reported')} {formatDateTime(task.startAt)}</span>
        </div>
        <div className="text-lg font-medium">{task.title}</div>
        <div className="text-sm text-gray-500">
          {task.customerName ?? '—'}{task.customerLevel ? `（${task.customerLevel}）` : ''}
          {' · '}{t('complaintTask.work.chat', {chat: task.chatName ?? '—'})}
          {task.trackingNos.length > 0 && ` · ${t('complaintTask.work.tracking')} ${task.trackingNos.join('、')}`}
          {task.assigneeName && ` · ${t('complaintTask.columns.assignee')} ${task.assigneeName}`}
        </div>
        <ProgressBar detail={detail}/>
        <MessageList detail={detail}/>
        <ResolutionView detail={detail}/>

        {processing && (
          <div className="flex flex-col gap-2">
            {!task.actualStartAt && (
              <Alert type="info" showIcon message={t('complaintTask.work.startFirst')}/>
            )}
            {/* SOP 提示浮窗位（P11）：内容后续由客服提供 */}
            <Input.TextArea
              rows={5}
              value={content}
              disabled={!task.actualStartAt}
              placeholder={t('complaintTask.work.placeholder') ?? ''}
              onChange={(e) => setContent(e.target.value)}
              onPaste={onPaste}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f) => (
                  <Tag key={f.fileKey} closable onClose={() => remove(f.fileKey)}>
                    {f.previewUrl ? <img src={f.previewUrl} alt={f.fileName} className="h-12 inline-block mr-1"/> : null}
                    {f.fileName}
                  </Tag>
                ))}
              </div>
            )}
            <Space>
              {!task.actualStartAt ? (
                <Button type="primary" loading={starting} onClick={onStart}>{t('complaintTask.work.start')}</Button>
              ) : (
                <Popconfirm title={t('complaintTask.work.confirmResolve')} onConfirm={onResolve}
                            okText={t('common.confirm')} cancelText={t('common.cancel')}>
                  <Button type="primary" className="!bg-green-600" loading={resolving} disabled={!content.trim() || uploading}>
                    {t('complaintTask.work.submit')}
                  </Button>
                </Popconfirm>
              )}
              <Upload accept="image/*" showUploadList={false} disabled={!task.actualStartAt}
                      beforeUpload={(file) => { upload(file); return false; }}>
                <Button disabled={!task.actualStartAt} loading={uploading}>{t('complaintTask.work.insertImage')}</Button>
              </Upload>
              <Upload showUploadList={false} disabled={!task.actualStartAt}
                      beforeUpload={(file) => { upload(file); return false; }}>
                <Button disabled={!task.actualStartAt}>{t('complaintTask.work.uploadFile')}</Button>
              </Upload>
            </Space>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default ComplaintTaskWorkPanel;
